import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import html2canvas from 'html2canvas';
import Report from './Report';
import hashGadgetIds from './hashGadgetIds';
import aguid from 'lib/aguid';
import STORE from 'store';
import { useNavigate } from 'react-router-dom';
import appRoutes, { buildReportShow } from '../../navigation/appRoutes';

import withoutNulls from '../../api/expression/withoutNulls';
import ReportContext from 'contexts/ReportContext';
import ReportGadgetsContext from 'contexts/ReportGadgetsContext';
import DashboardGadgetsContext from 'contexts/DashboardGadgetsContext';
import ReportDateDrillDownOptionsProvider from '../../contextProviders/ReportDateDrillDownOptionsProvider';
import moment from 'moment';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import AccountContext from '../../contexts/AccountContext';
import shareReportOrThrow from './shareReportOrThrow';
import ComparisonContext from '../../contexts/ComparisonContext';
import AnalyticsContext from '../../contexts/AnalyticsContext';
import CurrentUserContext from '../../contexts/CurrentUserContext';
import BonusPeriodsContext from '../../contexts/BonusPeriodsContext';
import { toDateRange } from '../../contexts/BoardContext';
import { DateTime } from 'luxon';
import useContentViewTracking from '../../hooks/useContentViewTracking';
import useRegisterInsightsContent from '../../hooks/useRegisterInsightsContent';
import updateRecentName from '../../api/recents/updateRecentName';
import updateFavouriteName from '../../api/favourites/updateFavouriteName';
import WorkSpaceContext from '../../contexts/WorkSpaceContext';
import useDataTypesOnReportCanvas from './useDataTypesOnReportCanvas';
import useLegacySavedFilter from '../../hooks/useLegacySavedFilter';
import delay from '../../delay';
import migrateRelativeDateRange from '../../migrateRelativeDateRange';
import CopyGadgetProvider from '../../contextProviders/CopyGadgetProvider';
import PopupContext from '../../contexts/PopupContext';
import DateInputContext from '../../contexts/DateInputContext';
import { parseSavedDateRange } from '../ConfigureDashboardGadget';
import withDateFilter from '../../hocs/withDateFIlter';
import useDateScope from '../../hooks/useDateScope';
import useWindowSize from '../../hooks/useWindowSize';
import PopupInnerContext from '../../contexts/PopupInnerContext';
import WallboardContext from '../../contexts/WallboardContext';
import AccountPickerContext from '../../contexts/AccountPickerContext';
import GqlClientProvider from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/GqlClientProvider';
import CloudFunctionClientContext from '../../contexts/CloudFunctionClientContext';
import FilterPlatesProvider from '../../contextProviders/FilterPlatesProvider';
import WeekStartsOnOverrideContext from 'contexts/WeekStartsOnOverrideContext';
import RolesContext from '../../contexts/RolesContext';
import useHasAccess from 'hooks/useHasAccess';
import TargetsAppContext from '../../screens/TargetsAppShow/TargetsAppConfigured/TargetsAppContext';
import EntityDetailsContext from '../../screens/EntityDetailsShow/EntityDetailsContext';
import filterTypeCheckers from '../FilterPlateForm/filterTypeCheckers';
import useToFixedFilters from '../../hooks/useToFixedFilters';
import { PortalsContext } from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import useAddNewReportToCurrentPortal from '../../hooks/useAddNewReportToCurrentPortal';
import useRefreshDatasetFreshnessOnMount from '../../hooks/useRefreshDatasetFreshnessOnMount';
import ToastContext from '../../contexts/ToastContext';
import useIsInvalidFilterInput from '../GroupVsRemainder/hooks/useIsInvalidFilterInput';
import usePopup from '../../hooks/usePopup';
import buildCardLayout from '../../contextProviders/WidgetGalleryProvider/buildCardLayout';
import findStartingY from '../../contextProviders/WidgetGalleryProvider/findStartingY';
import { getCanvasMode } from '../ReportCanvas';
import VisualisationCacheContext from 'contexts/VisualisationCacheContext';
import useQuickFilters from '../../hooks/useQuickFilters';
import toFilterInput from '../../toFilterInput';
import GroupedLoadingContext from '../../contexts/GroupedLoadingContext';
import createExportSavedFilterHelper from './createExportSavedFilter';
import RegisterReportInsightsView from './RegisterReportInsightsView';
import buildScheduledNotes from './buildScheduledNotes';
import usePublishPendingCsvs from './usePublishPendingCsvs';
import waitForGridExportCallbacks, {
  getExpectedGridExportCards,
} from './waitForGridExportCallbacks';

const getSavedFilterIdParam = (): string | undefined => {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('savedFilterId')) {
    return urlParams.get('savedFilterId') as string;
  }
  return undefined;
};

const hasEditingSearchQuery = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const isEditing =
    urlParams.has('editing') && urlParams.get('editing') === 'true';
  return isEditing;
};

const useInitialDateFilters = (
  report: ReportType,
  unSavedFilter?: UnSavedFilter,
  parentDashboardGadget?: DashboardGadget,
) => {
  const {
    setDateField,
    setDateRange,
    setRelativeDateRange,
    setAdvancedRelativeDateRange,
  } = useContext(DateInputContext);
  const { selectedBonusPeriod } = useContext(BonusPeriodsContext);
  const { workSpace } = useContext(WorkSpaceContext);

  // Initial Date Range
  useEffect(() => {
    const initialDateRange = (() => {
      if (
        selectedBonusPeriod &&
        workSpace &&
        workSpace.campaignType === 'driverBonus'
      ) {
        const dateRange = selectedBonusPeriod
          ? toDateRange(selectedBonusPeriod)
          : undefined;
        if (dateRange) {
          return dateRange;
        }
      }

      if (unSavedFilter) {
        if (
          unSavedFilter.relativeDateRange ||
          unSavedFilter.advancedRelativeDateRange ||
          unSavedFilter.dateRange
        ) {
          return unSavedFilter.dateRange;
        } else {
          return unSavedFilter.dateScope;
        }
      } else if (parentDashboardGadget) {
        if (parentDashboardGadget.dateRange) {
          return parseSavedDateRange(parentDashboardGadget.dateRange);
        } else {
          return undefined;
        }
      } else {
        return report.dateRange;
      }
    })();

    setDateRange(initialDateRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial Relative Date Range
  useEffect(() => {
    const initialRelativeDateRange = (() => {
      if (unSavedFilter) {
        return unSavedFilter.relativeDateRange;
      } else if (parentDashboardGadget) {
        return parentDashboardGadget.relativeDateRange
          ? migrateRelativeDateRange(parentDashboardGadget.relativeDateRange)
          : undefined;
      } else {
        return report.relativeDateRange;
      }
    })();

    setRelativeDateRange(initialRelativeDateRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial Advanced Relative Date Range
  useEffect(() => {
    const initialAdvancedRelativeDateRange = (() => {
      if (unSavedFilter) {
        return unSavedFilter.advancedRelativeDateRange;
      } else if (parentDashboardGadget) {
        return parentDashboardGadget.advancedRelativeDateRange;
      } else {
        return report.advancedRelativeDateRange;
      }
    })();

    setAdvancedRelativeDateRange(initialAdvancedRelativeDateRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial Date Field
  useEffect(() => {
    const initialDateField = (() => {
      if (parentDashboardGadget && parentDashboardGadget.dateField) {
        return parentDashboardGadget.dateField;
      } else if (report.dateField) {
        return report.dateField;
      } else if (unSavedFilter) {
        return unSavedFilter.dateField;
      } else {
        return 'date';
      }
    })();

    setDateField(initialDateField);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

const useReportDateSync = (
  report: ReportType,
  unSavedFilter?: UnSavedFilter,
  parentDashboardGadget?: DashboardGadget,
) => {
  const {
    setDateField,
    setDateRange,
    setRelativeDateRange,
    setAdvancedRelativeDateRange,
    isManuallyEntered,
  } = useContext(DateInputContext);
  const savedFilterId = getSavedFilterIdParam();
  const { savedFilter } = useLegacySavedFilter(savedFilterId);
  const { selectedBonusPeriod } = useContext(BonusPeriodsContext);
  const { workSpace } = useContext(WorkSpaceContext);

  useEffect(() => {
    if (isManuallyEntered) {
      return;
    }

    const bonusDateRange =
      selectedBonusPeriod &&
      workSpace &&
      workSpace.campaignType === 'driverBonus'
        ? toDateRange(selectedBonusPeriod)
        : undefined;

    if (bonusDateRange) {
      setDateRange(bonusDateRange);
      setRelativeDateRange(undefined);
      setAdvancedRelativeDateRange(undefined);
    } else if (savedFilter) {
      setDateRange(savedFilter.dateRange);
      setRelativeDateRange(savedFilter.relativeDateRange);
      setAdvancedRelativeDateRange(savedFilter.advancedRelativeDateRange);
      setDateField(savedFilter.dateField);
    } else if (unSavedFilter) {
      if (
        unSavedFilter.relativeDateRange ||
        unSavedFilter.advancedRelativeDateRange ||
        unSavedFilter.dateRange
      ) {
        setDateRange(unSavedFilter.dateRange);
        setRelativeDateRange(unSavedFilter.relativeDateRange);
        setAdvancedRelativeDateRange(unSavedFilter.advancedRelativeDateRange);
      } else {
        setDateRange(unSavedFilter.dateScope);
        setRelativeDateRange(undefined);
        setAdvancedRelativeDateRange(undefined);
      }

      setDateField(unSavedFilter.dateField);
    } else if (parentDashboardGadget) {
      setDateField(parentDashboardGadget.dateField);
      setDateRange(
        parentDashboardGadget.dateRange
          ? parseSavedDateRange(parentDashboardGadget.dateRange)
          : undefined,
      );
      setRelativeDateRange(
        parentDashboardGadget.relativeDateRange
          ? migrateRelativeDateRange(parentDashboardGadget.relativeDateRange)
          : undefined,
      );
      setAdvancedRelativeDateRange(
        parentDashboardGadget.advancedRelativeDateRange,
      );
    } else {
      setDateField(report.dateField);
      setDateRange(report.dateRange);
      setRelativeDateRange(report.relativeDateRange);
      setAdvancedRelativeDateRange(report.advancedRelativeDateRange);
    }
  }, [
    isManuallyEntered,
    unSavedFilter,
    selectedBonusPeriod,
    workSpace,
    setDateRange,
    setRelativeDateRange,
    setAdvancedRelativeDateRange,
    report.dateRange,
    report.relativeDateRange,
    report.advancedRelativeDateRange,
    savedFilter,
    parentDashboardGadget,
    setDateField,
    report.dateField,
  ]);
};

const ReportContainer = ({
  report,
  hideMenu,
  parentDashboardGadget,
  unSavedFilter,
  onDeleted,
  setReport,
  isValidationReport,
  forceHeadlessRender,
}: {
  report: ReportType;
  savedFilter?: LegacySavedFilter;
  hideMenu?: boolean;
  parentDashboardGadget?: DashboardGadget;
  unSavedFilter?: UnSavedFilter;
  onDeleted?: () => void;
  isPopup?: boolean;
  setReport?: React.Dispatch<React.SetStateAction<ReportType | undefined>>;
  isValidationReport?: boolean;
  // Set by the headless print screen so the export/ready hooks are exposed
  // without relying on wallboard context detection.
  forceHeadlessRender?: boolean;
}) => {
  const { selectedPortal } = useContext(PortalsContext);
  const navigate = useNavigate();
  const { api } = useContext(CloudFunctionClientContext);
  const { accountRef, selectedAccountId } = useContext(AccountPickerContext);
  const { isPopup } = useContext(PopupInnerContext);
  const { scope, canvas, drillDowns, access } = report;
  useContentViewTracking({
    type: 'report',
    typeId: report.id,
    name: report.name,
  });
  useRefreshDatasetFreshnessOnMount();

  const { isOpen: isPopupReportOpen, origin: popupOrigin } =
    useContext(PopupContext);
  const targetsAppContext = useContext(TargetsAppContext);
  const { workSpace } = useContext(WorkSpaceContext);
  const { isWallboard } = useContext(WallboardContext);
  // Publish to the AI Insights Chat so it can scope answers to this report.
  // Skipped in popups/wallboards (not the user's primary, in-focus content).
  useRegisterInsightsContent(
    isPopup || isWallboard
      ? null
      : {
          entityType: 'report',
          entityId: report.id,
          entityName: report.name,
          content: { contentType: 'Report', content: report },
        },
  );
  const { currentPermissions } = useContext(RolesContext);
  const entityDetailsContext = useContext(EntityDetailsContext);
  const savedFilterId = getSavedFilterIdParam();
  const { savedFilter } = useLegacySavedFilter(savedFilterId);
  // Edit/admin chrome only collapses on phone-width windows. The canvas itself
  // reflows by its own width (see ReportCanvas getLayoutTier).
  const { useMobileLayout: isMobile } = useWindowSize();
  const addNewReportToCurrentPortal = useAddNewReportToCurrentPortal();

  const {
    dateField,
    dateRange,
    relativeDateRange,
    advancedRelativeDateRange,
    setDateRange,
    setRelativeDateRange,
    setAdvancedRelativeDateRange,
  } = useContext(DateInputContext);
  useInitialDateFilters(report, unSavedFilter, parentDashboardGadget);
  useReportDateSync(report, unSavedFilter, parentDashboardGadget);
  const dateScopeForEmail = useDateScope({});
  const { id: accountId, weekStartsOn } = useContext(AccountContext);
  useEffect(() => {}, []);
  const { trackEvent } = useContext(AnalyticsContext);
  const { showToast } = useContext(ToastContext);
  const { reportGadgetsLookup } = useContext(ReportGadgetsContext);
  const { dashboardGadgetsLookup } = useContext(DashboardGadgetsContext);
  const { putReportGadgetInCache, cache } = useContext(
    VisualisationCacheContext,
  );

  const toDrillDowns = useToFixedFilters();

  const quickFilters = useQuickFilters(`report: ${report.id}`);
  const [selectedQuickFilters, setSelectedQuickFilters] = useState<
    LegacySavedFilter[]
  >([]);
  const netQuickFilters = useMemo(() => {
    return selectedQuickFilters.map((qf) =>
      toFilterInput(qf, [], toDrillDowns),
    );
  }, [selectedQuickFilters, toDrillDowns]);

  const [currentCanvas, setCurrentCanvas] = useState<Canvas>(() => canvas);
  const dataTypes = useDataTypesOnReportCanvas(currentCanvas);
  const { setDataTypes } = useContext(DateInputContext);
  const [currentScope, setCurrentScope] = useState<FilterPlateType[]>(() => {
    if (unSavedFilter) {
      return unSavedFilter.scope;
    } else if (parentDashboardGadget) {
      return parentDashboardGadget.scope;
    } else {
      return scope;
    }
  });
  const [currentDrillDowns, setCurrentDrillDowns] = useState<FilterPlateType[]>(
    () => {
      if (unSavedFilter) {
        return unSavedFilter.drillDowns;
      } else if (parentDashboardGadget) {
        return parentDashboardGadget.drillDowns;
      } else if (drillDowns) {
        return drillDowns;
      } else {
        return [];
      }
    },
  );
  const [isEntityFilterEnabled, setIsEntityFilterEnabled] = useState<boolean>(
    () => {
      if (report) {
        return !!report.isEntityFilterEnabled;
      }
      return false;
    },
  );
  const [isDefaultFiltersDisabled, setIsDefaultFiltersDisabled] =
    useState<boolean>(() => {
      if (parentDashboardGadget) {
        return !!parentDashboardGadget.isDefaultFiltersDisabled;
      }

      return !!report.isDefaultFiltersDisabled;
    });
  const [isEditing, setIsEditing] = useState<boolean>(hasEditingSearchQuery());
  const [hasTracked, setHasTracked] = useState<boolean>(false);
  const [hasLeftDefaultState, setHasLeftDefaultState] =
    useState<boolean>(false);
  const [currentComparison, setCurrentComparison] = useState<
    PersistedComparisonType | undefined
  >(() => {
    if (unSavedFilter) {
      return unSavedFilter.comparison;
    } else if (parentDashboardGadget) {
      return parentDashboardGadget.comparison;
    } else {
      return report.comparison;
    }
  });
  const [weekStartsOnOverride, setWeekStartsOnOverride] = useState<
    WeekStartsOn | undefined
  >(report.weekStartsOnOverride);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [gridExportCallbacks, setGridExportCallbacks] = useState<
    GridExportCallback[]
  >([]);
  // Latest registered callbacks, readable from inside an export that is already
  // running (the headless export waits for late-registering grids).
  const gridExportCallbacksRef = useRef<GridExportCallback[]>([]);
  gridExportCallbacksRef.current = gridExportCallbacks;
  // Always points at the current-render exportReport. The headless export hook
  // (window.__fleetopsExportReport) is registered once at mount, so without this
  // it would call the mount-time closure — reading the initial (empty)
  // gridExportCallbacks and pre-savedFilter scope, so no CSVs are attached and
  // the email filtering/date is stale. Calling through the ref uses the latest
  // closure once gadgets have registered their CSV callbacks and filters applied.
  const exportReportRef = useRef<
    (
      userIds: string[],
      notes: string,
      senderIdOverride?: string,
      headless?: {
        pdfPath: string;
        exportId: string;
        reportImagePath?: string;
        missingGadgets?: string[];
      },
    ) => Promise<ReportSchedules.HeadlessExportResult | void>
  >();
  const [isRenderComplete, setIsRenderComplete] = useState(false);
  const [renderLoadingHasBegun, setRenderLoadingHasBegun] = useState(false);
  const GroupedLoading = useContext(GroupedLoadingContext);
  const currentUser = useContext(CurrentUserContext);
  const currentUserId = currentUser.id;
  // The headless scheduled-report renderer authenticates as a dedicated wallboard
  // user, so (as on a wallboard) the report renders and exposes the export/ready
  // hooks the renderer drives. On the report route WallboardContext.isWallboard is
  // false, so we key off the current user's type.
  const isHeadlessRender =
    isWallboard || !!currentUser.isWallboardUser || !!forceHeadlessRender;
  const hasAccess = useHasAccess();
  const hasInvalidFilters = useIsInvalidFilterInput(canvas);
  const {
    isOpen: isConfigureOpen,
    open: openConfigure,
    close: closeConfigure,
  } = usePopup();
  const [editingReportGadget, setEditingReportGadget] = useState<
    ReportGadgets.ReportGadget | undefined
  >();

  const onAddVisualization = useCallback(() => {
    setEditingReportGadget(undefined);
    openConfigure();
  }, [openConfigure]);

  // Track when loading begins so we don't mark complete before any data loads.
  useEffect(() => {
    if (GroupedLoading.isLoading) {
      setRenderLoadingHasBegun(true);
    }
  }, [GroupedLoading.isLoading]);

  // Deterministic "report is painted and ready" signal the headless renderer awaits.
  // Created once; resolved when data has loaded AND the browser has actually painted
  // (fonts ready + two animation frames) — this replaces the old fixed 2s settle
  // timer, which was racy in both directions.
  const reportReadyRef = useRef<{
    promise: Promise<void>;
    resolve: () => void;
  }>();
  if (!reportReadyRef.current) {
    let resolveReady: () => void = () => {};
    const promise = new Promise<void>((res) => {
      resolveReady = res;
    });
    reportReadyRef.current = { promise, resolve: resolveReady };
  }

  useEffect(() => {
    if (
      isRenderComplete ||
      !renderLoadingHasBegun ||
      GroupedLoading.isLoading
    ) {
      return;
    }
    let cancelled = false;
    const markReady = async () => {
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }
      // Two frames guarantees the post-load layout has been committed and painted.
      await new Promise<void>((r) => requestAnimationFrame(() => r()));
      await new Promise<void>((r) => requestAnimationFrame(() => r()));
      if (cancelled) return;
      setIsRenderComplete(true);
      reportReadyRef.current?.resolve();
    };
    markReady();
    return () => {
      cancelled = true;
    };
  }, [GroupedLoading.isLoading, isRenderComplete, renderLoadingHasBegun]);

  // Expose the headless render hooks (ready promise + export trigger) when the
  // report is being driven by the headless renderer or shown on a wallboard.
  useEffect(() => {
    if (!isHeadlessRender) return;
    window.__fleetopsReportReady = reportReadyRef.current?.promise;
    // Return the export promise so the renderer can await completion before
    // closing the page (previously fire-and-forget, which could truncate the export).
    window.__fleetopsExportReport = ({
      recipientUserIds,
      senderId,
      notes,
      pdfPath,
      reportImagePath,
      exportId,
      missingGadgets,
    }) =>
      // Call through the ref so we invoke the latest exportReport closure (with
      // the registered CSV callbacks + applied savedFilter scope), not the
      // mount-time one captured when this effect first ran.
      exportReportRef.current?.(
        recipientUserIds,
        notes,
        senderId,
        pdfPath && exportId
          ? { pdfPath, exportId, reportImagePath, missingGadgets }
          : undefined,
      );
    return () => {
      window.__fleetopsReportReady = undefined;
      window.__fleetopsExportReport = undefined;
    };
    // exportReport is invoked via exportReportRef (kept current each render), so
    // the effect only needs to re-run when the headless flag flips.
  }, [isHeadlessRender]);

  const onEditReportGadget = useCallback(
    (gadget: ReportGadgets.ReportGadget) => {
      // Always use the freshest version from cache
      const freshGadget = reportGadgetsLookup[gadget.id] || gadget;
      setEditingReportGadget(freshGadget);
      openConfigure();
    },
    [openConfigure, reportGadgetsLookup],
  );

  const startEditing = useCallback(() => {
    trackEvent('Report - Edit Started', {
      reportId: report.id,
      reportName: report.name,
    });
    setCurrentDrillDowns([]);
    setCurrentScope(scope);
    setDateRange(report.dateRange);
    setRelativeDateRange(report.relativeDateRange);
    setAdvancedRelativeDateRange(report.advancedRelativeDateRange);
    setWeekStartsOnOverride(report.weekStartsOnOverride);
    setIsEditing(true);
  }, [
    report.id,
    report.name,
    report.dateRange,
    report.relativeDateRange,
    report.advancedRelativeDateRange,
    report.weekStartsOnOverride,
    scope,
    setAdvancedRelativeDateRange,
    setDateRange,
    setRelativeDateRange,
    trackEvent,
  ]);

  const onReportGadgetSaved = useCallback(
    (gadget: ReportGadgets.ReportGadget) => {
      putReportGadgetInCache(gadget);
      if (!editingReportGadget) {
        const cards = currentCanvas.cards;
        const canvasMode = getCanvasMode({ isMobile });
        const card: CanvasCard.ReportGadgetCard = {
          layout: buildCardLayout(0, findStartingY(cards, canvasMode)),
          content: {
            type: 'Report Gadget',
            reportGadgetId: gadget.id,
          },
        };
        const newCanvas: Canvas = {
          ...currentCanvas,
          cards: [...cards, card],
        };
        setCurrentCanvas(newCanvas);

        const newReport: ReportType = {
          ...report,
          canvas: newCanvas,
          updatedOn: DateTime.utc().toISO(),
          updatedBy: currentUser.id,
          generatorConfig: report.generatorConfig
            ? { ...report.generatorConfig, isManuallyEdited: true }
            : report.generatorConfig,
        };
        STORE.getReportsRef({ accountId: selectedAccountId })
          .doc(report.id)
          .set(withoutNulls(newReport))
          .then(() => {
            if (setReport) {
              setReport(withoutNulls(newReport) as ReportType);
            }
          });

        trackEvent('Report - Edit - Card Added');
        setIsEditing(true);
      } else if (report.generatorConfig) {
        STORE.getReportsRef({ accountId: selectedAccountId })
          .doc(report.id)
          .update({
            'generatorConfig.isManuallyEdited': true,
            updatedOn: DateTime.utc().toISO(),
            updatedBy: currentUser.id,
          });
      }
      closeConfigure();
    },
    [
      closeConfigure,
      currentCanvas,
      currentUser.id,
      editingReportGadget,
      isMobile,
      putReportGadgetInCache,
      report,
      selectedAccountId,
      setCurrentCanvas,
      setReport,
      trackEvent,
    ],
  );

  useEffect(() => {
    if (hasInvalidFilters) {
      showToast(
        'Unsupported entity filters detected, Group Vs Remainder gadgets disabled',
      );
    }
  }, [hasInvalidFilters, showToast]);

  useEffect(() => {
    setDataTypes(dataTypes);
  }, [dataTypes, setDataTypes]);

  useEffect(() => {
    if (hasTracked) {
      return;
    }
    if (isPopupReportOpen) {
      trackEvent('Popup Report - Opened', {
        reportName: report.name,
        reportId: report.id,
        accessType: report.access.type,
        origin: popupOrigin,
      });
    } else {
      trackEvent('Report - Opened', {
        reportName: report.name,
        reportId: report.id,
        accessType: report.access.type,
        source: workSpace ? 'Workspace' : 'Report',
      });
    }

    setHasTracked(true);
  }, [
    hasTracked,
    isPopupReportOpen,
    report.access.type,
    report.id,
    report.name,
    trackEvent,
    popupOrigin,
    workSpace,
  ]);

  useEffect(() => {
    setHasLeftDefaultState(currentDrillDowns.length !== 0);
  }, [currentDrillDowns]);

  useEffect(() => {
    if (unSavedFilter) {
      setCurrentScope(unSavedFilter.scope);
      setCurrentDrillDowns(unSavedFilter.drillDowns);
    } else if (parentDashboardGadget) {
      setCurrentScope(parentDashboardGadget.scope);
      setCurrentDrillDowns(parentDashboardGadget.drillDowns);
      setIsDefaultFiltersDisabled(
        !!parentDashboardGadget.isDefaultFiltersDisabled,
      );
    } else if (savedFilter) {
      setCurrentScope(savedFilter.scope);
      setCurrentDrillDowns(savedFilter.drillDowns);
    } else {
      setCurrentScope(scope);
      setIsDefaultFiltersDisabled(!!report.isDefaultFiltersDisabled);
    }
  }, [
    parentDashboardGadget,
    report.isDefaultFiltersDisabled,
    savedFilter,
    scope,
    unSavedFilter,
  ]);

  const reset = () => {
    setSelectedQuickFilters([]);
    if (parentDashboardGadget) {
      setCurrentScope(parentDashboardGadget.scope);
      setCurrentDrillDowns(parentDashboardGadget.drillDowns);
      setIsDefaultFiltersDisabled(
        !!parentDashboardGadget.isDefaultFiltersDisabled,
      );
    } else if (unSavedFilter) {
      setCurrentScope(unSavedFilter.scope);
      setCurrentDrillDowns(unSavedFilter.drillDowns);
    } else {
      setCurrentScope(scope);
      setCurrentDrillDowns([]);
      setIsDefaultFiltersDisabled(!!report.isDefaultFiltersDisabled);
    }
  };

  useEffect(() => {
    if (
      access &&
      !isPopup &&
      !workSpace &&
      !isValidationReport &&
      !targetsAppContext &&
      !entityDetailsContext &&
      !isWallboard
    ) {
      if (
        !hasAccess({
          access,
          resource: report,
          type: 'report',
          typeId: report.id,
        })
      ) {
        navigate(appRoutes.home);
      }
    }
  }, [
    entityDetailsContext,
    access,
    currentUser,
    isPopup,
    isWallboard,
    report,
    workSpace,
    currentPermissions,
    isValidationReport,
    hasAccess,
    targetsAppContext,
    navigate,
  ]);

  const createExportSavedFilter = useCallback(
    (source: 'report-export' | 'report-schedule' = 'report-export') =>
      createExportSavedFilterHelper({
        source,
        dateField,
        currentScope,
        currentDrillDowns,
        relativeDateRange,
        dateRange,
        advancedRelativeDateRange,
        accountId: selectedAccountId,
      }),
    [
      dateField,
      currentScope,
      currentDrillDowns,
      relativeDateRange,
      dateRange,
      advancedRelativeDateRange,
      selectedAccountId,
    ],
  );

  const onEditSave = () => {
    const newReport: ReportType = {
      ...report,
      canvas: currentCanvas,
      scope: currentScope,
      dateRange,
      relativeDateRange,
      advancedRelativeDateRange,
      comparison: currentComparison,
      updatedOn: DateTime.utc().toISO(),
      updatedBy: currentUser.id,
      dateField,
      weekStartsOnOverride,
      isEntityFilterEnabled,
      isDefaultFiltersDisabled,
      generatorConfig: report.generatorConfig
        ? { ...report.generatorConfig, isManuallyEdited: true }
        : report.generatorConfig,
    };
    trackEvent('Report - Edit Saved', {
      reportId: report.id,
      reportName: report.name,
    });
    setIsEditing(false);
    STORE.getReportsRef({ accountId: selectedAccountId })
      .doc(report.id)
      .set(withoutNulls(newReport))
      .then(() => {
        setIsEditing(false);
        if (setReport) {
          setReport(withoutNulls(newReport));
        }
        if (hasEditingSearchQuery()) {
          navigate({ search: '' });
        }
      });
  };

  const saveEditAs = async (name: string, labelIds: string[]) => {
    const copiedCards = await deepCopyReportGadgetCards(currentCanvas.cards);
    const newReport: ReportType = {
      ...report,
      name,
      labelIds,
      id: aguid(),
      canvas: { ...currentCanvas, cards: copiedCards },
      scope: currentScope,
      dateRange,
      relativeDateRange,
      advancedRelativeDateRange,
      comparison: currentComparison,
      dateField,
      weekStartsOnOverride,
      createdBy: currentUser.id,
      createdOn: DateTime.utc().toISO(),
      updatedBy: currentUser.id,
      updatedOn: DateTime.utc().toISO(),
      isEntityFilterEnabled,
      isDefaultFiltersDisabled,
    };

    STORE.getReportsRef({ accountId: selectedAccountId })
      .doc(newReport.id)
      .set(newReport)
      .then(() => {
        const requests = [
          addNewReportToCurrentPortal(newReport.id),
          updateRecentName({
            type: 'report',
            typeId: report.id,
            newName: name,
            userId: currentUserId,
            accountRef,
          }),
          updateFavouriteName({
            type: 'report',
            typeId: report.id,
            newName: name,
            userId: currentUserId,
            accountRef,
          }),
        ];
        Promise.all(requests).then(() => {
          navigate(
            buildReportShow({
              id: newReport.id,
              isEditing: false,
              portal: selectedPortal,
            }),
          );
        });
      });
  };

  const onEditCancel = () => {
    trackEvent('Report - Edit Cancelled', {
      reportId: report.id,
      reportName: report.name,
    });
    setIsEditing(false);
    setCurrentCanvas(canvas);
    setCurrentScope(scope);
    setIsDefaultFiltersDisabled(!!report.isDefaultFiltersDisabled);
    setCurrentDrillDowns([]);
    setWeekStartsOnOverride(report.weekStartsOnOverride);
    if (hasEditingSearchQuery()) {
      navigate({ search: '' });
    }
  };

  const deepCopyReportGadgetCards = useCallback(
    async (cards: CanvasCard.Card[]): Promise<CanvasCard.Card[]> => {
      return Promise.all(
        cards.map(async (card) => {
          if (card.content.type !== 'Report Gadget') return card;
          const originalGadget = cache.reportGadgets.get(
            card.content.reportGadgetId,
          );
          if (!originalGadget) return card;
          const newGadgetId = aguid();
          const newGadget: ReportGadgets.ReportGadget = {
            ...originalGadget,
            id: newGadgetId,
          };
          await STORE.visualisations
            .getReportGadgetsRef({ accountId: selectedAccountId })
            .doc(newGadgetId)
            .set(newGadget);
          return {
            ...card,
            content: { ...card.content, reportGadgetId: newGadgetId },
          } as CanvasCard.ReportGadgetCard;
        }),
      );
    },
    [cache.reportGadgets, selectedAccountId],
  );

  const saveReportAs = async (name: string, labelIds: string[]) => {
    const copiedCards = await deepCopyReportGadgetCards(report.canvas.cards);
    const newReport: ReportType = {
      ...report,
      name,
      labelIds,
      dateField,
      dateRange,
      relativeDateRange,
      advancedRelativeDateRange,
      id: aguid(),
      weekStartsOnOverride,
      isEntityFilterEnabled,
      isDefaultFiltersDisabled,
      canvas: { ...report.canvas, cards: copiedCards },
    };

    await STORE.getReportsRef({ accountId: selectedAccountId })
      .doc(newReport.id)
      .set(newReport)
      .then(() => {
        addNewReportToCurrentPortal(newReport.id).then(() => {
          navigate(
            buildReportShow({ id: newReport.id, portal: selectedPortal }),
          );
        });
      });
  };

  const onDelete = useCallback(() => {
    STORE.getReportsRef({ accountId: selectedAccountId })
      .doc(report.id)
      .update({ isHidden: true })
      .then(() => {
        trackEvent('Report - Deleted');
        if (onDeleted) {
          onDeleted();
        } else {
          navigate(appRoutes.loggedIn.reports);
        }
      });
  }, [navigate, onDeleted, report.id, selectedAccountId, trackEvent]);

  // A scheduled render waits for every grid's CSV
  usePublishPendingCsvs({
    isPdfExport: !!forceHeadlessRender,
    cards: currentCanvas.cards,
    gridExportCallbacks,
    reportGadgetsLookup,
    dashboardGadgetsLookup,
  });

  const exportReport = async (
    userIds: string[],
    notes: string,
    senderIdOverride?: string,
    // Set by the headless renderer: it has already produced and uploaded the
    // report PDF and inline preview image, so we skip the in-page JPEG capture,
    // upload CSVs under the same exportId, and share the PDF + preview.
    headless?: {
      pdfPath: string;
      exportId: string;
      reportImagePath?: string;
      // Cards that hadn't loaded when the renderer captured the report
      missingGadgets?: string[];
    },
  ): Promise<ReportSchedules.HeadlessExportResult | void> => {
    const reportElement = document.getElementById('report');
    if (!reportElement) {
      return;
    }
    if (!currentUserId) {
      return;
    }
    trackEvent('Report - Exported', {
      reportName: report.name,
      reportId: report.id,
    });

    const savedFilterId = await createExportSavedFilter();

    const prefix = report.name;
    const postfix = moment.utc().format('Do MMM YYYY');
    const exportName = `${prefix} - ${postfix}`.replace(/ /g, '');
    const exportId = headless?.exportId ?? aguid();
    const storage = firebase.storage().ref();
    const pathBase = `accounts/${accountId}/exports/${exportId}`;

    // Headless: no one is watching, so wait for every grid gadget to register
    // its CSV rather than sending whatever has registered so far.
    const { callbacks: csvExportCallbacks, missingCsvs } = headless
      ? await waitForGridExportCallbacks({
          expectedCards: getExpectedGridExportCards({
            cards: currentCanvas.cards,
            reportGadgetsLookup,
            dashboardGadgetsLookup,
          }),
          getGridExportCallbacks: () => gridExportCallbacksRef.current,
        })
      : { callbacks: gridExportCallbacks, missingCsvs: [] };

    // Upload CSV grid exports (shared by both interactive and headless paths).
    let waitTime = 5000;
    const excelPaths = [] as string[];
    // Two gadgets can share a name + date range (e.g. a grid and a pivot of the
    // same metric), which yields an identical CSV filename. Without a unique
    // suffix the second upload overwrites the first and both excelPaths point at
    // one file, so a matrix's CSV silently goes missing from the email.
    const usedFileNames = new Set<string>();
    for (let i = 0; i < csvExportCallbacks.length; i++) {
      const fileData = await csvExportCallbacks[i].callback();
      let fileName = fileData.fileName;
      if (usedFileNames.has(fileName)) {
        let suffix = 2;
        while (usedFileNames.has(`${fileData.fileName} (${suffix})`)) {
          suffix += 1;
        }
        fileName = `${fileData.fileName} (${suffix})`;
      }
      usedFileNames.add(fileName);
      const filePath = `${pathBase}/${fileName}.csv`;
      waitTime += 5000;

      const response = await storage
        .child(filePath)
        .putString(fileData.content, undefined, {
          contentType: 'text/csv',
        });
      excelPaths.push(response.metadata.fullPath);
    }

    const buildAndShare = async ({
      reportPath,
      pdfPath,
    }: {
      reportPath: string;
      pdfPath?: string;
    }) => {
      const emailDateRange = `${dateScopeForEmail.startDate} - ${dateScopeForEmail.endDate}`;

      const filtering = toDrillDowns({
        plates: currentDrillDowns,
        variableDrillDowns: [],
      })
        .filter(filterTypeCheckers.isTextFilter)
        .map(
          (d) => `${d.field} ${d.keywordValues ? `(${d.keywordValues})` : ''}`,
        )
        .toString()
        .replace(/,/g, ', ');

      const reportLink = window.location.href.includes('dashboards')
        ? `${window.location.origin}${window.location.pathname}?popupReportId=${report.id}&savedFilterId=${savedFilterId}`
        : `${window.location.origin}${buildReportShow({
            id: report.id,
            portal: selectedPortal,
          })}?savedFilterId=${savedFilterId}`;

      const exportData = {
        id: exportId,
        name: `${report.name} - ${moment.utc().format('Do MMM YYYY')}`,
        senderId: senderIdOverride ?? currentUserId,
        reportPath,
        pdfPath,
        excelPaths: excelPaths.length < 1 ? [] : excelPaths,
        dateRange: emailDateRange,
        filtering: filtering === '' ? 'None' : filtering,
        reportLink,
        reportName: report.name,
        userIds,
        notes: headless
          ? buildScheduledNotes({
              notes,
              missingGadgets: headless.missingGadgets ?? [],
              contentLabel: 'report',
            })
          : notes === ''
            ? 'None'
            : notes,
        // The headless renderer only drives scheduled deliveries; manual shares
        // use the interactive path. Lets the email service pick the body copy.
        isScheduled: !!headless,
      } as ReportExportBody;
      // On the headless (scheduled) path a non-OK share throws so the renderer
      // releases its emailDelivered claim and Cloud Tasks retries, instead of a
      // failed send being silently recorded as a success.
      await shareReportOrThrow({ exportData, api, headless: !!headless });
    };

    if (headless) {
      // The renderer already uploaded the PDF and the inline preview image; just
      // share them (reportPath is the image the email embeds in the body).
      await buildAndShare({
        reportPath: headless.reportImagePath ?? '',
        pdfPath: headless.pdfPath,
      });
      // The renderer records these on the run
      return { excelPaths, missingCsvs };
    }

    // Interactive path: capture a JPEG snapshot of the report.
    const reportPath = `${pathBase}/${exportName}.jpg`;
    await html2canvas(reportElement).then((canvas) => {
      canvas.toBlob(async (blob) => {
        if (!blob) {
          return false;
        }
        const reportRef = storage.child(reportPath);
        const response = await reportRef.put(blob);
        await buildAndShare({ reportPath: response.metadata.fullPath });
        return;
      });
    });
    await delay(waitTime);
  };
  // Keep the headless export hook pointing at this render's exportReport.
  exportReportRef.current = exportReport;
  // The menu's export: an interactive share, which resolves with nothing
  const exportReportInteractive = async (
    userIds: string[],
    notes: string,
    senderIdOverride?: string,
  ) => {
    await exportReport(userIds, notes, senderIdOverride);
  };

  const onScroll = useCallback(() => {
    const filterBar = document.getElementById('report-filters');
    if (!filterBar || isPopup) {
      return;
    }

    if (filterBar.getBoundingClientRect().top <= 10) {
      if (filterBar.style.boxShadow === '') {
        filterBar.style.boxShadow = 'inset 0px -1px 0px rgba(0, 0, 0, 0.08)';
      }
    } else {
      if (filterBar.style.boxShadow !== '') {
        filterBar.style.boxShadow = '';
      }
    }
  }, [isPopup]);

  return (
    <ReportContext.Provider
      value={{
        report,
        isEditing,
        currentScope,
        currentDrillDowns,
        reset,
        isExporting,
        setIsExporting,
        gridExportCallbacks,
        setGridExportCallbacks,
        onAddVisualization,
        onEditReportGadget,
        setCurrentCanvas,
        setIsEditing,
        quickFilters,
        selectedQuickFilters,
        setSelectedQuickFilters,
        isHeadlessRender,
        isPdfExport: !!forceHeadlessRender,
      }}
    >
      <GqlClientProvider>
        <CopyGadgetProvider>
          <ComparisonContext.Provider
            value={{ currentComparison, setCurrentComparison }}
          >
            <ReportDateDrillDownOptionsProvider startOfWeek={weekStartsOn}>
              <FilterPlatesProvider
                drillDowns={currentDrillDowns}
                setDrillDowns={setCurrentDrillDowns}
                scope={currentScope}
                setScope={setCurrentScope}
                dataTypes={dataTypes}
                isDefaultFiltersDisabled={isDefaultFiltersDisabled}
                setIsDefaultFiltersDisabled={setIsDefaultFiltersDisabled}
                quickFilters={netQuickFilters}
              >
                <WeekStartsOnOverrideProviderExceptForPopups
                  isPopup={isPopup}
                  weekStartsOnOverride={weekStartsOnOverride}
                >
                  <RegisterReportInsightsView
                    reportId={report.id}
                    cards={currentCanvas.cards}
                    isDefaultFiltersDisabled={isDefaultFiltersDisabled}
                    isDisabled={isPopup || isWallboard || isHeadlessRender}
                  >
                    <Report
                      report={report}
                      currentCanvas={currentCanvas}
                      setCurrentCanvas={setCurrentCanvas}
                      isEditing={isEditing}
                      onEditSave={onEditSave}
                      onEditCancel={onEditCancel}
                      saveReportAs={saveReportAs}
                      saveEditAs={saveEditAs}
                      startEditing={startEditing}
                      exportReport={exportReportInteractive}
                      isRenderComplete={isRenderComplete}
                      onDelete={onDelete}
                      hasLeftDefaultState={hasLeftDefaultState}
                      onAddVisualization={onAddVisualization}
                      hideMenu={hideMenu}
                      weekStartsOnOverride={weekStartsOnOverride}
                      setWeekStartsOnOverride={setWeekStartsOnOverride}
                      isMobile={isMobile}
                      reportId={report.id}
                      isPopup={isPopup}
                      onScroll={onScroll}
                      isValidationReport={isValidationReport}
                      isTargetsApp={!!targetsAppContext}
                      isEntityApp={!!entityDetailsContext}
                      isWorkspace={!!workSpace}
                      isEntityFilterEnabled={isEntityFilterEnabled}
                      setIsEntityFilterEnabled={setIsEntityFilterEnabled}
                      dataTypes={dataTypes}
                      isConfigureOpen={isConfigureOpen}
                      closeConfigure={closeConfigure}
                      editingReportGadget={editingReportGadget}
                      onReportGadgetSaved={onReportGadgetSaved}
                      onEditReportGadget={onEditReportGadget}
                    />
                  </RegisterReportInsightsView>
                </WeekStartsOnOverrideProviderExceptForPopups>
              </FilterPlatesProvider>
            </ReportDateDrillDownOptionsProvider>
          </ComparisonContext.Provider>
        </CopyGadgetProvider>
      </GqlClientProvider>
    </ReportContext.Provider>
  );
};

// We don't want this behaviour in a popup report.
// In that context, the dashboard gadget is the source of
// override, overwriting the report's override.
// This is UX/tech debt.
const WeekStartsOnOverrideProviderExceptForPopups = ({
  weekStartsOnOverride,
  isPopup,
  children,
}: {
  weekStartsOnOverride?: WeekStartsOn;
  isPopup: boolean;
  children: JSX.Element | JSX.Element[];
}) => {
  if (isPopup) {
    return <>{children}</>;
  }
  return (
    <WeekStartsOnOverrideContext.Provider
      value={{
        weekStartsOnOverride,
      }}
    >
      {children}
    </WeekStartsOnOverrideContext.Provider>
  );
};

const ReportContainerWithKey = (
  props: React.ComponentProps<typeof ReportContainer>,
) => {
  const gadgetKey = props.report.generatorConfig
    ? hashGadgetIds(props.report.generatorConfig.gadgetIds)
    : '';
  return <ReportContainer key={gadgetKey} {...props} />;
};

export default withDateFilter(ReportContainerWithKey, {});
